import * as React from 'react';
import { Button, MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { continueSignIn, onSignInNeeded, resetAndReload } from '../auth';

/**
 * The way out when start-up takes too long or fails: try again, or start over in this browser
 * (forget the sign-in and the app's saved state, then sign in again). Nothing in the library or
 * its settings is touched.
 */
export function StuckHelp(props: { onRetry: () => void }): React.ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 460, alignItems: 'center', textAlign: 'center' }}>
      <span className="small" style={{ fontSize: 14 }}>
        If it stays like this, try again. If that doesn't help, reset: you'll sign in again, and nothing in your library or settings changes.
      </span>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button appearance="primary" onClick={props.onRetry}>Try again</Button>
        <Button onClick={resetAndReload}>Reset and sign in again</Button>
      </div>
    </div>
  );
}

/** A loading screen that never just spins: after a while it explains and offers a way out. */
export function Loading(props: { label: string; onRetry: () => void; afterMs?: number }): React.ReactElement {
  const [slow, setSlow] = React.useState<boolean>(false);
  React.useEffect(() => {
    const t = setTimeout(() => setSlow(true), props.afterMs ?? 12000);
    return () => clearTimeout(t);
  }, [props.afterMs]);
  return (
    <main className="center-page" style={{ flexDirection: 'column', gap: 20 }}>
      <Spinner label={slow ? 'This is taking longer than usual…' : props.label} />
      {slow && <StuckHelp onRetry={props.onRetry} />}
    </main>
  );
}

export function StartupError(props: { message: string; onRetry: () => void }): React.ReactElement {
  return (
    <main className="center-page" style={{ flexDirection: 'column', gap: 20 }}>
      <MessageBar intent="error"><MessageBarBody>{props.message}</MessageBarBody></MessageBar>
      <StuckHelp onRetry={props.onRetry} />
    </main>
  );
}

/**
 * When Microsoft needs the person during background work (nobody clicked), the app doesn't leave
 * the page by surprise: this banner asks, and Continue goes to Microsoft and back to this screen.
 */
export function SignInBanner(): React.ReactElement | null {
  const [scopes, setScopes] = React.useState<string[] | undefined>(undefined);
  React.useEffect(() => onSignInNeeded(s => setScopes(s)), []);
  if (!scopes) {
    return null;
  }
  return (
    <div role="alert" style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, maxWidth: 'calc(100% - 32px)' }}>
      <MessageBar intent="warning">
        <MessageBarBody>
          Microsoft needs you to sign in again to keep going.{' '}
          <Button size="small" appearance="primary" onClick={() => { void continueSignIn(scopes); }}>Continue</Button>{' '}
          <Button size="small" appearance="transparent" onClick={() => setScopes(undefined)}>Later</Button>
        </MessageBarBody>
      </MessageBar>
    </div>
  );
}
